// eslint-disable-next-line import/no-relative-packages
import { formatCurrencyRounded, currencySymbol } from '../../../utils/currency';

/**
 * Format a number as currency.
 *
 * NOTE: forecast-v3 uses rounded-to-whole-unit currency (no cents) because the
 * grid is dense — see callers in PnLGrid, AnalysisTab, etc. We delegate to the
 * shared util and keep the (parens-for-negative) idiom callers expect.
 *
 * @param value      - amount to format
 * @param showParens - render negatives as ``(1,234)`` instead of ``-1,234``
 * @param currency   - ISO-4217 code (e.g. ``'JPY'``). Defaults to USD when null.
 *                     Callers in single-location editors should pass
 *                     ``location.currency`` here. Dashboard-style consumers
 *                     should source from ``useReportingCurrency``.
 */
export function formatCurrency(
  value: number,
  showParens = true,
  currency?: string | null,
): string {
  if (value === 0) return `${currencySymbol(currency)}0`;

  const absValue = Math.abs(value);
  const formatted = formatCurrencyRounded(absValue, currency);

  if (value < 0) {
    return showParens ? `(${formatted})` : `-${formatted}`;
  }
  return formatted;
}

/**
 * Format a number as a percentage
 * @param value - The decimal value (0.152 = 15.2%)
 * @param decimals - Number of decimal places (default: 1)
 */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format a number with +/- prefix
 * @param value    - The number to format
 * @param currency - ISO-4217 code. Defaults to USD when null.
 */
export function formatVariance(value: number, currency?: string | null): string {
  const formatted = formatCurrency(value, false, currency);
  return value > 0 ? `+${formatted}` : formatted;
}

/**
 * Format a variance as a percentage with +/- prefix
 * @param value - The decimal value
 */
export function formatVariancePercent(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatPercent(value)}`;
}

/**
 * Format a date for display
 * @param date - ISO date string or Date object
 * @param format - 'short' | 'long' | 'month-year'
 */
export function formatDate(date: string | Date, format: 'short' | 'long' | 'month-year' = 'short'): string {
  const d = typeof date === 'string' ? new Date(date) : date;

  if (format === 'month-year') {
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  if (format === 'long') {
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param date - ISO date string or Date object
 */
export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatDate(d, 'short');
}

/**
 * Format a number with thousands separator
 * @param value - The number to format
 */
export function formatNumber(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * Truncate text with ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length before truncation
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 3)}...`;
}

/**
 * Get initials from a name
 * @param name - Full name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

/**
 * Format month range for display
 * @param startMonth - Start month (0-11)
 * @param endMonth - End month (0-11)
 */
export function formatMonthRange(startMonth: number, endMonth: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (startMonth === endMonth) {
    return months[startMonth];
  }

  return `${months[startMonth]}-${months[endMonth]}`;
}
